import { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { LogIn } from 'lucide-react'
import { IconMenu, IconSparkle, IconX } from './Icons.jsx'
import { useModals } from '../contexts/ModalContext.jsx'
import { BRAND_TAGLINE_TIGHT } from '../constants/brand.js'
import { LOGOS } from '../pages/home/constants/photos.js'
import { INTAKE_PATH, PATIENT_LOGIN_URL } from '../pages/home/constants/links.js'

/** Site treatment pages — route labels only, not product data. */
const TREATMENT_NAV = [
  { to: '/weight-loss', label: 'Weight Loss' },
  { to: '/peptides', label: 'Peptides' },
  { to: '/mens-health', label: "Men's Hormones" },
  { to: '/womens-hormones', label: "Women's Hormones" },
  { to: '/wellness', label: 'Wellness' },
  { to: '/recovery', label: 'Recovery' },
  { to: '/labs', label: 'Labs' },
  { to: '/urgent-care', label: 'Urgent Care' },
]

const NAV_LINKS = [
  { to: '/pricing', label: 'Pricing' },
  { to: '/providers', label: 'Providers' },
  { to: '/blog', label: 'Learn' },
  { to: '/about', label: 'About' },
]

function LoginLink({ className = '', iconSize = 15, onClick, labelClassName = '' }) {
  return (
    <a
      href={PATIENT_LOGIN_URL}
      target="_blank"
      rel="noopener noreferrer"
      onClick={onClick}
      className={className}
    >
      <LogIn size={iconSize} strokeWidth={2.35} aria-hidden="true" className="shrink-0" />
      <span className={labelClassName}>Login</span>
    </a>
  )
}

export function Header() {
  const [open, setOpen] = useState(false)
  const [treatmentsOpen, setTreatmentsOpen] = useState(false)
  const [mobileTreatments, setMobileTreatments] = useState(false)
  const [scrolled, setScrolled] = useState(false)
  const { openBarry } = useModals()
  const rootRef = useRef(null)

  function closeMenu() {
    setOpen(false)
    setMobileTreatments(false)
  }

  useEffect(() => {
    const prev = document.body.style.overflow
    document.body.style.overflow = open ? 'hidden' : ''
    return () => {
      document.body.style.overflow = prev
    }
  }, [open])

  useEffect(() => {
    // Hysteresis avoids sticky height/shadow thrash near the threshold.
    const onScroll = () => {
      const y = window.scrollY
      setScrolled((prev) => (prev ? y > 8 : y > 40))
    }
    // Initial read waits until after the first paint: reading scrollY right
    // after React commits the page forces a synchronous layout (Lighthouse
    // "forced reflow", ~55 ms on mobile). rAF → setTimeout lands post-paint.
    let timeout
    const frame = requestAnimationFrame(() => {
      timeout = setTimeout(onScroll, 0)
    })
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => {
      cancelAnimationFrame(frame)
      clearTimeout(timeout)
      window.removeEventListener('scroll', onScroll)
    }
  }, [])

  useEffect(() => {
    if (!open) return undefined
    function onPointerDown(e) {
      if (rootRef.current && !rootRef.current.contains(e.target)) closeMenu()
    }
    function onKeyDown(e) {
      if (e.key === 'Escape') closeMenu()
    }
    document.addEventListener('pointerdown', onPointerDown)
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('pointerdown', onPointerDown)
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [open])

  return (
    <div ref={rootRef} className="sticky top-0 z-50 isolate">
      <header
        className={`relative z-20 flex h-[76px] w-full items-center justify-between gap-3 bg-white px-5 transition-[box-shadow] sm:gap-4 sm:px-6 lg:px-8 ${
          scrolled ? 'shadow-[0_6px_24px_rgba(0,128,128,0.10)]' : 'shadow-[0_1px_0_rgba(0,0,0,0.04)]'
        }`}
      >
        <Link
          to="/"
          className="flex min-w-0 shrink flex-col items-start justify-center gap-0.5"
          onClick={closeMenu}
        >
          <img
            src={LOGOS.primary}
            srcSet={`${LOGOS.primary360} 360w, ${LOGOS.primary} 560w`}
            sizes="(max-width: 640px) 189px, 252px"
            width={560}
            height={89}
            alt="HealBerry"
            className="h-10 w-auto max-[640px]:h-[30px]"
          />
          <span className="hidden font-sans text-[10px] font-medium leading-[1.25] text-[#6B6B6B] sm:block sm:max-w-[320px] sm:text-[11px]">
            {BRAND_TAGLINE_TIGHT}
          </span>
        </Link>

        <nav className="hidden items-center gap-5 min-[1121px]:flex">
          <div
            className="relative"
            onMouseEnter={() => setTreatmentsOpen(true)}
            onMouseLeave={() => setTreatmentsOpen(false)}
          >
            <button
              type="button"
              className="inline-flex items-center gap-1.5 border-0 bg-transparent py-1.5 font-display text-[13px] font-bold leading-none text-[#191A17] transition hover:text-teal-600"
              aria-expanded={treatmentsOpen}
              aria-haspopup="true"
            >
              Treatments
              <svg
                width="12"
                height="12"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.6"
                strokeLinecap="round"
                strokeLinejoin="round"
                className={`shrink-0 transition-transform duration-200 ease-out ${treatmentsOpen ? 'rotate-180' : ''}`}
                aria-hidden="true"
              >
                <path d="m6 9 6 6 6-6" />
              </svg>
            </button>
            {treatmentsOpen && (
              <div className="absolute left-1/2 top-full z-50 pt-3.5 -translate-x-1/2">
                <div className="grid min-w-[238px] gap-0.5 rounded-2xl border border-[#E8F1F0] bg-white p-2.5 shadow-[0_12px_40px_rgba(0,128,128,0.14)]">
                  {TREATMENT_NAV.map((item) => (
                    <Link
                      key={item.to}
                      to={item.to}
                      className="whitespace-nowrap rounded-[10px] px-3.5 py-2.5 font-display text-[13.5px] font-bold text-[#4A4A4A] transition hover:bg-teal-50 hover:text-teal-600"
                      onClick={() => setTreatmentsOpen(false)}
                    >
                      {item.label}
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>
          {NAV_LINKS.map((link) => (
            <Link
              key={link.to}
              to={link.to}
              className="relative py-1.5 font-display text-[13px] font-bold leading-none text-[#191A17] transition hover:text-teal-600 after:absolute after:bottom-0 after:left-0 after:h-0.5 after:w-0 after:bg-teal-600 after:transition-[width] hover:after:w-full"
            >
              {link.label}
            </Link>
          ))}
        </nav>

        <div className="hidden items-center gap-2.5 min-[1121px]:flex">
          <LoginLink
            className="inline-flex h-9 items-center gap-1.5 rounded-full border border-[#DCEAEA] bg-[#F4FAF9] px-3.5 font-display text-[13px] font-bold text-teal-700 transition hover:border-teal-500/45 hover:bg-teal-50 hover:text-teal-800"
            iconSize={14}
          />
          <button
            type="button"
            onClick={openBarry}
            className={`rounded-full border-[1.5px] border-plum-600 px-[15px] py-[7px] font-display text-[13px] font-extrabold text-plum-600 transition hover:bg-plum-600 hover:text-white ${
              scrolled ? 'opacity-100' : 'opacity-90'
            }`}
          >
            <IconSparkle /> Find my treatment
          </button>
          <Link to={INTAKE_PATH} className="btn btn-berry btn-sm">
            Start free visit
          </Link>
        </div>

        <div className="flex shrink-0 items-center gap-1.5 min-[1121px]:hidden">
          <LoginLink
            className="inline-flex h-10 min-w-10 items-center justify-center gap-1.5 rounded-full border border-[#DCEAEA] bg-[#F4FAF9] px-2.5 font-display text-[12.5px] font-bold text-teal-700 transition active:bg-teal-50 sm:px-3"
            iconSize={15}
            labelClassName="max-[360px]:sr-only"
          />
          <button
            type="button"
            className="flex h-10 w-10 items-center justify-center rounded-full p-1.5 text-[#191A17] transition hover:bg-stone-50"
            aria-label={open ? 'Close menu' : 'Open menu'}
            aria-expanded={open}
            onClick={() => (open ? closeMenu() : setOpen(true))}
          >
            {open ? <IconX size={24} /> : <IconMenu size={24} />}
          </button>
        </div>
      </header>

      {/* Absolute (not fixed) under sticky root — avoids fixed-in-sticky collapse */}
      <div
        className={`absolute inset-x-0 top-full z-10 overflow-y-auto border-t border-[#E8F1F0] bg-white px-5 py-6 sm:px-6 min-[1121px]:hidden ${
          open
            ? 'pointer-events-auto h-[calc(100dvh-76px)] translate-y-0 opacity-100'
            : 'pointer-events-none h-0 translate-y-[-6px] opacity-0'
        }`}
        style={{
          transition:
            'opacity 280ms ease, transform 280ms ease, height 0ms linear ' +
            (open ? '0ms' : '280ms'),
        }}
        // `inert`, not aria-hidden: also pulls the closed menu's links and
        // buttons out of the tab order, so keyboard users can't land in it.
        inert={!open}
      >
        <div className="flex min-h-full flex-col">
          <button
            type="button"
            className="flex w-full items-center justify-between border-b border-[#E8F1F0] py-3.5 font-display text-[20px] font-extrabold text-[#191A17]"
            onClick={() => setMobileTreatments((v) => !v)}
          >
            Treatments
            <span className="text-base">{mobileTreatments ? '▴' : '▾'}</span>
          </button>
          {mobileTreatments && (
            <div className="flex flex-col border-b border-[#E8F1F0] pb-2 pl-3">
              {TREATMENT_NAV.map((item) => (
                <Link
                  key={item.to}
                  to={item.to}
                  onClick={closeMenu}
                  className="py-2.5 font-display text-[15px] font-bold text-[#4A4A4A]"
                >
                  {item.label}
                </Link>
              ))}
            </div>
          )}
          {NAV_LINKS.map((link) => (
            <Link
              key={link.to}
              to={link.to}
              onClick={closeMenu}
              className="border-b border-[#E8F1F0] py-3.5 font-display text-[20px] font-extrabold text-[#191A17]"
            >
              {link.label}
            </Link>
          ))}

          <div className="mt-auto flex flex-col gap-3 pt-6">
            <LoginLink
              onClick={closeMenu}
              iconSize={17}
              className="inline-flex h-12 w-full items-center justify-center gap-2 rounded-full border border-[#DCEAEA] bg-[#F4FAF9] font-display text-[15px] font-bold text-teal-700 transition active:bg-teal-50"
            />
            <button
              type="button"
              onClick={() => {
                closeMenu()
                openBarry()
              }}
              className="inline-flex h-12 w-full items-center justify-center gap-1 rounded-full border-[1.5px] border-plum-600 font-display text-[15px] font-extrabold text-plum-600 transition active:bg-plum-50"
            >
              <IconSparkle /> Find my treatment
            </button>
            <Link
              to={INTAKE_PATH}
              onClick={closeMenu}
              className="btn btn-berry btn-lg w-full"
            >
              Start free visit
            </Link>
          </div>
        </div>
      </div>
    </div>
  )
}
